import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { userIsAdmin } from '../../../Store/Reducers';
import CompanyGoalCarousel from './CompanyGoalCarousel';
import GoalsSection from '../../Settings/Profile/GoalsSection';
import { Target, Rocket, Trophy, Settings } from 'lucide-react';
import { Button } from '../../../Components/UI/button';

export default function GoalsDashboardCard({ isMobile }) {
  const { data: agencyData } = useSelector(state => state.agencyData);
  const isAdmin = useSelector(userIsAdmin);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const goalText = agencyData?.Goal || agencyData?.CompanyGoal || '';

  let mGoal = '';
  let qGoal = '';
  let yGoal = '';
  try {
    const parsed = JSON.parse(goalText);
    if (Array.isArray(parsed) && parsed[0]) {
      const obj = parsed[0];
      mGoal = obj.M || '';
      qGoal = obj.Q || '';
      yGoal = obj.Y || '';
    } else if (parsed && typeof parsed === 'object') {
      mGoal = parsed.M || parsed.monthly || '';
      qGoal = parsed.Q || parsed.quarterly || '';
      yGoal = parsed.Y || parsed.yearly || '';
    } else {
      mGoal = goalText;
    }
  } catch (e) {
    mGoal = goalText;
  }

  const hasGoals = (mGoal && mGoal.trim()) || (qGoal && qGoal.trim()) || (yGoal && yGoal.trim());

  // If no goals are set: show a preview of the carousel/card at the same size, with placeholder
  // copy standing in for real goal text, and a "Set Goals" action in the top-right.
  if (!hasGoals) {
    if (isAdmin) {
      const placeholderCopy = 'Set a target to keep your team aligned.';

      if (isMobile) {
        return (
          <div className="px-4">
            <div
              style={{
                backgroundColor: "var(--bg-white)",
                boxShadow: "var(--shadow-md)",
              }}
              className="relative rounded-xl p-3 pt-[10px] overflow-hidden bg-white cursor-pointer active:opacity-90 transition-opacity"
              onClick={() => setIsDialogOpen(true)}
            >
              <div className="flex items-center justify-between mb-2 gap-2">
                <span className="text-text-color text-[18px] font-semibold text-left">
                  Goal for the Month
                </span>
                <Button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setIsDialogOpen(true); }}
                  className="text-xs font-semibold h-7 px-3 shrink-0"
                >
                  Set Goals
                </Button>
              </div>

              <div className="my-1">
                <p className="text-[15px] text-text-color opacity-80 leading-normal font-semibold text-left">
                  {placeholderCopy}
                </p>
              </div>

              <div className="justify-center flex items-center mt-3">
                <div className="flex gap-1 w-[30%]">
                  {[0, 1, 2].map(i => (
                    <div key={i} className="flex-1 rounded-full overflow-hidden" style={{ height: 3, background: '#f0f0f0' }} />
                  ))}
                </div>
              </div>
            </div>
            <GoalsSection open={isDialogOpen} onClose={() => setIsDialogOpen(false)} />
          </div>
        );
      }

      const placeholderLabels = ['Goal for the Month', 'Goal for the Quarter', 'Goal for the Year'];
      return (
        <div className="col-span-full">
          <div
            style={{
              backgroundColor: "var(--bg-white)",
              boxShadow: "var(--shadow-md)",
            }}
            className="bg-white rounded-xl px-5 py-4 flex flex-col gap-3 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setIsDialogOpen(true)}
          >
            <div className="flex items-center justify-end">
              <Button
                type="button"
                onClick={(e) => { e.stopPropagation(); setIsDialogOpen(true); }}
                className="text-xs font-semibold h-7 px-3"
              >
                Set Goals
              </Button>
            </div>
            <div className="flex flex-col md:flex-row gap-6">
              {placeholderLabels.map((label, i) => (
                <div
                  key={label}
                  className={`flex-1 flex items-start ${i > 0 ? 'md:border-s md:border-gray-100 md:ps-6' : ''}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold mb-1" style={{ color: '#BF5700' }}>{label}</p>
                    <p className="text-[14px] text-text-color opacity-85 leading-snug font-semibold">{placeholderCopy}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <GoalsSection open={isDialogOpen} onClose={() => setIsDialogOpen(false)} />
        </div>
      );
    }
    return null;
  }

  // If mobile view, render the Carousel:
  if (isMobile) {
    const goals = [
      { period: 'monthly', text: mGoal },
      { period: 'quarterly', text: qGoal },
      { period: 'yearly', text: yGoal }
    ].filter(g => g.text && g.text.trim());

    if (!goals.length) return null;
    return (
      <>
        <CompanyGoalCarousel
          goals={goals}
          isAdmin={isAdmin}
          onConfigure={() => setIsDialogOpen(true)}
        />
        {isAdmin && <GoalsSection open={isDialogOpen} onClose={() => setIsDialogOpen(false)} />}
      </>
    );
  }

  // If desktop view, render side-by-side cards:
  const rawGoals = [
    { period: 'monthly', text: mGoal },
    { period: 'quarterly', text: qGoal },
    { period: 'yearly', text: yGoal }
  ].filter(g => g.text && g.text.trim());

  const getDesktopGoalTheme = (goal, idx, total) => {
    let presetKey = 'orange';
    if (total === 1) {
      presetKey = 'orange';
    } else if (total === 2) {
      presetKey = idx === 0 ? 'orange' : 'blue';
    } else if (total === 3) {
      if (goal.period === 'monthly') presetKey = 'blue';
      else if (goal.period === 'quarterly') presetKey = 'orange';
      else if (goal.period === 'yearly') presetKey = 'green';
    }

    const presets = {
      orange: { accent: '#BF5700', bg: '#BF570022', Icon: Target },
      blue: { accent: '#0d1f2d', bg: '#0d1f2d22', Icon: Rocket },
      green: { accent: '#0b5d1e', bg: '#0b5d1e22', Icon: Trophy }
    };

    let label = 'Goal for the Month';
    if (goal.period === 'quarterly') label = 'Goal for the Quarter';
    else if (goal.period === 'yearly') label = 'Goal for the Year';

    return { ...presets[presetKey], label };
  };

  const singleGoal = rawGoals.length === 1;

  return (
    <div className="col-span-full">
      <div
        style={{
          backgroundColor: "var(--bg-white)",
          boxShadow: "var(--shadow-md)",
        }}
        className={`relative bg-white rounded-xl px-5 py-4 flex flex-col md:flex-row gap-6 ${isAdmin && singleGoal ? 'cursor-pointer hover:shadow-lg transition-shadow' : ''}`}
        onClick={isAdmin && singleGoal ? () => setIsDialogOpen(true) : undefined}
      >
        {isAdmin && (
          <button
            type="button"
            aria-label="Configure goals"
            onClick={(e) => { e.stopPropagation(); setIsDialogOpen(true); }}
            className="absolute top-3 right-3 p-1.5 hover:bg-gray-100 transition-colors text-sahi-primary-color sahi-radius"
          >
            <Settings className="h-5 w-5" />
          </button>
        )}
        {rawGoals.map((g, i) => {
          const theme = getDesktopGoalTheme(g, i, rawGoals.length);
          return (
            <div
              key={g.period}
              className={`flex-1 flex items-start ${
                i > 0 ? 'md:border-s md:border-gray-100 md:ps-6' : ''
              }`}
            >
              <div className="min-w-0 flex-1 animate-slide-in">
                <p className="text-[13px] font-semibold mb-1" style={{ color: theme.accent }}>
                  {theme.label}
                </p>
                <p className="text-[14px] text-text-color opacity-85 leading-snug font-semibold">{g.text}</p>
              </div>
            </div>
          );
        })}
      </div>
      {isAdmin && <GoalsSection open={isDialogOpen} onClose={() => setIsDialogOpen(false)} />}
    </div>
  );
}
